import { useState, type RefObject } from "react"

import {
  Button,
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from "@coval/ui"
import { Download, Loader2, Pause, Play, X } from "lucide-react"

import { formatTime, SECONDS_PER_HOUR } from "@/lib/audio-player-utils"

import { parseAudioMarkerTime } from "./audioRegionController"

const PLAYBACK_RATES = [1, 1.5, 2]

function playbackRateLabel(playbackRate: number) {
  return `${playbackRate}x`
}

/** Speed button that opens a popover listing the available playback rates. */
function PlaybackRateControl({
  compact = false,
  onPlaybackRateChange,
  playbackRate,
}: {
  compact?: boolean
  onPlaybackRateChange: (playbackRate: number) => void
  playbackRate: number
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={compact ? undefined : "ghost"}
          className={
            compact
              ? "rounded-lg bg-surface-0 px-3 py-2 text-xs font-semibold text-coval-text transition-all duration-200 hover:bg-surface-1"
              : "h-6! shrink-0 rounded-full px-2 text-xs font-semibold"
          }
          aria-label={`Playback speed: ${playbackRateLabel(playbackRate)}`}
        >
          {playbackRateLabel(playbackRate)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        className="w-24 p-1"
        role="radiogroup"
        aria-label="Playback speed"
      >
        {PLAYBACK_RATES.map((rate) => (
          <PopoverItem
            key={rate}
            role="radio"
            aria-checked={rate === playbackRate}
            checked={rate === playbackRate}
            onClick={() => {
              onPlaybackRateChange(rate)
              setOpen(false)
            }}
          >
            {playbackRateLabel(rate)}
          </PopoverItem>
        ))}
      </PopoverContent>
    </Popover>
  )
}

export function AudioMarkerModal({
  canSubmit,
  currentTime,
  dialogRef,
  hoverTime,
  idPrefix,
  isEditing,
  label,
  labelInputRef,
  manualTime,
  onClose,
  onHoverTimeChange,
  onLabelChange,
  onSubmit,
  onTimeChange,
  open,
}: {
  canSubmit: boolean
  currentTime: number
  dialogRef: RefObject<HTMLDivElement | null>
  hoverTime: number | null
  idPrefix: string
  isEditing: boolean
  label: string
  labelInputRef: RefObject<HTMLInputElement | null>
  manualTime: string
  onClose: () => void
  onHoverTimeChange: (time: number) => void
  onLabelChange: (label: string) => void
  onSubmit: () => void
  onTimeChange: (time: string) => void
  open: boolean
}) {
  if (!open) {
    return null
  }
  const modalTitle = isEditing ? "Edit Interruption" : "Add Interruption"
  const buttonText = isEditing ? "Update Interruption" : "Add Interruption"
  const submitOnEnter = () => {
    if (canSubmit && label.trim()) {
      onSubmit()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${idPrefix}-add-marker-modal-title`}
        className="w-96 max-w-sm rounded-lg bg-white p-6"
      >
        <div className="mb-4 flex items-center justify-between">
          <h3
            id={`${idPrefix}-add-marker-modal-title`}
            className="text-lg font-semibold text-gray-900"
          >
            {modalTitle}
          </h3>
          <Button
            onClick={onClose}
            className="bg-transparent p-1 text-gray-400 shadow-none hover:bg-transparent hover:text-gray-600"
            aria-label="Close modal"
          >
            <X size={20} />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor={`${idPrefix}-marker-label-input`}
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Label
            </label>
            <input
              ref={labelInputRef}
              id={`${idPrefix}-marker-label-input`}
              type="text"
              value={label}
              onChange={(event) => onLabelChange(event.target.value)}
              placeholder="Enter interruption description..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  submitOnEnter()
                }
              }}
            />
          </div>

          <div>
            <label
              htmlFor={`${idPrefix}-marker-time-input`}
              className="mb-2 block text-sm font-normal text-gray-700"
            >
              Time (MM:SS)
            </label>
            <input
              id={`${idPrefix}-marker-time-input`}
              type="text"
              value={manualTime}
              onChange={(event) => onTimeChange(event.target.value)}
              placeholder="0:00"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(event) => {
                if (event.key !== "Enter") {
                  return
                }
                const parsedTime = parseAudioMarkerTime(manualTime)
                if (parsedTime !== null) {
                  onHoverTimeChange(parsedTime)
                  submitOnEnter()
                }
              }}
            />
          </div>

          <div className="text-sm text-gray-500">
            Current hover time: {formatTime(hoverTime ?? currentTime)}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={onSubmit}
              disabled={!canSubmit || !label.trim()}
              className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
            >
              {buttonText}
            </Button>
            <Button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function CompactAudioControls({
  isDownloading,
  isPlaying,
  onDownload,
  onPlaybackRateChange,
  onPlayPause,
  playbackRate,
}: {
  isDownloading: boolean
  isPlaying: boolean
  onDownload: () => void
  onPlaybackRateChange: (playbackRate: number) => void
  onPlayPause: () => void
  playbackRate: number
}) {
  return (
    <>
      <Button
        onClick={onPlayPause}
        className="rounded-lg bg-surface-0 p-2 text-coval-text transition-all duration-200 hover:bg-surface-1"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </Button>
      <PlaybackRateControl
        compact
        onPlaybackRateChange={onPlaybackRateChange}
        playbackRate={playbackRate}
      />
      <DownloadButton
        compact
        isDownloading={isDownloading}
        onDownload={onDownload}
      />
    </>
  )
}

/** Elapsed-over-total readout for the compact player, which has no flanking labels. */
export function CompactAudioTime({
  currentTime,
  duration,
}: {
  currentTime: number
  duration: number
}) {
  return (
    <div className="relative flex items-center justify-center gap-4 pb-3 pt-3">
      <div className="pl-4 font-mono text-sm font-light text-coval-text">
        {currentTime > 0 ? `${formatTime(currentTime)} / ` : ""}
        {formatTime(duration)}
      </div>
    </div>
  )
}

/**
 * Reads out one end of the playhead range: elapsed on the left, total on the
 * right. Both ends reserve the same width, which is what keeps the control
 * cluster between them centred on the player rather than on the space left over.
 * Recordings of an hour or more format as `HH:MM:SS`, which needs the wider
 * reservation to stay clear of the cluster.
 */
function AudioTimeLabel({
  align,
  seconds,
  wide,
}: {
  align: "left" | "right"
  seconds: number | null
  wide: boolean
}) {
  return (
    <span
      className={`${wide ? "w-16" : "w-10"} shrink-0 font-mono text-xs tabular-nums text-coval-text-muted ${
        align === "left" ? "text-left" : "text-right"
      }`}
    >
      {seconds === null ? "" : formatTime(seconds)}
    </span>
  )
}

export function FullAudioControls({
  elapsedSeconds,
  isDownloading,
  isPlaying,
  leadingInsetPixels = 0,
  onDownload,
  onPlaybackRateChange,
  onPlayPause,
  playbackRate,
  totalSeconds,
}: {
  elapsedSeconds: number | null
  isDownloading: boolean
  isPlaying: boolean
  /**
   * Whatever the waveform row puts to the left of the well — its speaker
   * gutter. Repeated here so both time labels stay on the well's edges and the
   * control cluster stays centred on the well rather than on the card.
   */
  leadingInsetPixels?: number
  onDownload: () => void
  onPlaybackRateChange: (playbackRate: number) => void
  onPlayPause: () => void
  playbackRate: number
  totalSeconds: number | null
}) {
  // Both ends take their width from the total, so the pair stays symmetric even
  // while the elapsed side is still reading in the shorter `MM:SS` form.
  const wideTimeLabels = (totalSeconds ?? 0) >= SECONDS_PER_HOUR

  return (
    <div
      className="flex items-center px-4 pb-3 pt-3"
      style={
        leadingInsetPixels
          ? { paddingLeft: `calc(1rem + ${leadingInsetPixels}px)` }
          : undefined
      }
    >
      <AudioTimeLabel
        align="left"
        seconds={elapsedSeconds}
        wide={wideTimeLabels}
      />
      <div className="flex flex-1 items-center justify-center gap-2">
        <PlaybackRateControl
          onPlaybackRateChange={onPlaybackRateChange}
          playbackRate={playbackRate}
        />
        <Button
          onClick={onPlayPause}
          icon
          // `icon` already sizes this to a 28px square, which is the diameter
          // the play control wants, so it only has to ask for the round shape.
          className="shrink-0 rounded-full text-surface-default [&>svg]:fill-current"
          tooltip={isPlaying ? "Pause" : "Play"}
        >
          {/* A right-pointing triangle carries its mass behind the apex and
            low against its baseline, so a box-centred one reads as sitting
            left and low. Pause is symmetric and needs no such nudge. */}
          {isPlaying ? (
            <Pause />
          ) : (
            <Play className="translate-x-[1px] translate-y-[-1px]" />
          )}
        </Button>
        <DownloadButton isDownloading={isDownloading} onDownload={onDownload} />
      </div>
      <AudioTimeLabel
        align="right"
        seconds={totalSeconds}
        wide={wideTimeLabels}
      />
    </div>
  )
}

function DownloadButton({
  compact = false,
  isDownloading,
  onDownload,
}: {
  compact?: boolean
  isDownloading: boolean
  onDownload: () => void
}) {
  return (
    <Button
      onClick={onDownload}
      disabled={isDownloading}
      variant={compact ? undefined : "ghost"}
      icon={!compact}
      className={
        compact
          ? "rounded-lg bg-surface-0 p-2 text-coval-text transition-all duration-200 hover:bg-surface-1 disabled:opacity-50"
          : "h-6! w-6! shrink-0 rounded-full p-0"
      }
      // Named the same way in both modes: `icon` is a runtime choice, and the
      // label has to hold when the button is showing its text instead.
      aria-label="Download audio"
      tooltip="Download audio"
    >
      {isDownloading ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Download size={16} />
      )}
    </Button>
  )
}
